import { initializeApp } from "firebase/app";
import { getAuth, Auth, browserSessionPersistence } from "firebase/auth";
import {
  initializeAppCheck,
  CustomProvider,
} from "firebase/app-check";
import { createTurnstileProvider } from "../utils/turnstileProvider";

// Validate environment variables
const validateEnvVariables = () => {
  const requiredEnvVars = [
    "REACT_APP_FIREBASE_API_KEY",
    "REACT_APP_FIREBASE_AUTH_DOMAIN",
    "REACT_APP_FIREBASE_PROJECT_ID",
    "REACT_APP_FIREBASE_STORAGE_BUCKET",
    "REACT_APP_FIREBASE_MESSAGING_SENDER_ID",
    "REACT_APP_FIREBASE_APP_ID",
  ];

  // Add Turnstile-specific environment variables when App Check is enabled
  if (process.env.REACT_APP_ENABLE_APPCHECK === "true") {
    requiredEnvVars.push(
      "REACT_APP_TURNSTILE_SITE_KEY",
      "REACT_APP_TURNSTILE_TOKEN_EXCHANGE_URL"
    );
  }

  const missingVars = requiredEnvVars.filter(
    (varName) => !process.env[varName]?.trim()
  );

  if (missingVars.length > 0) {
    throw new Error(
      `Missing or empty required environment variables: ${missingVars.join(
        ", "
      )}\n` +
        "Please check your .env file and ensure all variables are properly set"
    );
  }

  // Validate API key format
  const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
  if (!/^AIza[0-9A-Za-z-_]{35}$/.test(apiKey || "")) {
    throw new Error("Invalid Firebase API key format");
  }

  // Validate Project ID format
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
  if (!/^[a-z0-9-]+$/.test(projectId || "")) {
    throw new Error("Invalid Firebase Project ID format");
  }
};

// Validate environment variables before initializing Firebase
validateEnvVariables();

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

let auth: Auth;
let app;
let appCheckEnabled = false;

try {
  // Initialize Firebase
  app = initializeApp(firebaseConfig);

  // Initialize App Check only when explicitly enabled and Turnstile keys are provided
  if (
    process.env.REACT_APP_ENABLE_APPCHECK === "true" &&
    process.env.REACT_APP_TURNSTILE_SITE_KEY &&
    process.env.REACT_APP_TURNSTILE_TOKEN_EXCHANGE_URL
  ) {
    try {
      // Using custom provider that implements official Cloudflare approach
      // (Workaround for Create React App compilation issues with official package)
      const providerOptions = createTurnstileProvider(
        process.env.REACT_APP_TURNSTILE_TOKEN_EXCHANGE_URL!,
        process.env.REACT_APP_TURNSTILE_SITE_KEY!
      );
      const provider = new CustomProvider(providerOptions);

      initializeAppCheck(app, {
        provider,
        isTokenAutoRefreshEnabled: true,
      });
      appCheckEnabled = true;
    } catch (appCheckError) {
      console.error("App Check initialization failed:", appCheckError);
    }
  } else {
    console.error("App Check is required but disabled or missing environment variables");
  }

  // Get Auth instance
  auth = getAuth(app);

  // Configure auth persistence
  auth.setPersistence(browserSessionPersistence).catch((error) => {
    console.error("Error setting auth persistence:", error);
  });
} catch (error) {
  console.error("Error initializing Firebase:", error);
  throw error;
}

export { auth, app, appCheckEnabled };
